/**
 * Query language — async label→id resolution (cluster only).
 *
 * EXPERIMENTAL (lewis/query-lang-labels, O-2470). A user types a human LABEL on
 * an id-valued dim; this layer turns it into something the (sync) compiler can
 * express as a FilterExpr:
 *
 *   cluster:<text>    → clusterResolver(text) → cluster id(s) → cluster_subtree IN/EQ
 *   cluster:<number>  → used as an id directly (no resolution)
 *   endpoint:<text>   → NOT resolved here — the compiler matches it by hostname
 *                       (ep.hostname ILIKE) over a PROMPT → ENDPOINT join, so
 *                       there's no client-side id lookup and no 1024-id IN cap.
 *
 * Only cluster needs async resolution (the cluster-tree load), so this sits
 * between the sync parser and the sync compiler. The resolver is INJECTED (the
 * real one lives in queryLangResolvers.js, browser-only) so this module + its
 * tests stay free of the cluster-tree / analytics-client deps.
 *
 * A cluster label that resolves to NO ids compiles to a never-match (a sentinel
 * id), not a dropped clause — `cluster:nonesuch` must return nothing.
 */

import { parseQuery } from "./queryLang.js";
import { compileQuery } from "./queryLangCompile.js";

// Sentinel cluster id that matches no row, used when a label resolves to zero
// ids so the clause fails closed (real cluster ids are >= 0).
const NO_CLUSTER = "-1";

// Walk every pred node in the AST (depth-first), calling visit(node).
function walkPreds(node, visit) {
  if (!node || typeof node !== "object") return;
  if (node.type === "pred") visit(node);
  if (node.child) walkPreds(node.child, visit);
  for (const c of node.children ?? []) walkPreds(c, visit);
}

function walkText(node, visit) {
  if (!node || typeof node !== "object") return;
  if (node.type === "text") visit(node);
  if (node.child) walkText(node.child, visit);
  for (const c of node.children ?? []) walkText(c, visit);
}

const isNumeric = (v) => v !== "" && Number.isFinite(Number(v));

const SKIPPED_CONVERSATION_SEARCH = {
  too_common: (term) => `"${term}" is in too many conversations to search. Try a more specific term or a shorter range.`,
  too_short: (term) => `"${term}" is too short to search conversations.`,
};

/**
 * Parse `queryText`, resolve label clauses on id-valued dims to ids, then
 * compile. Returns the compiled `{ filter, joins, errors, warnings }` (same
 * shape as compileQuery) plus `resolved` (the label→ids the UI can surface).
 *
 * opts.clusterResolver(text) → Promise<string[]> (or sync array). Missing
 * resolver → the cluster label fails closed (no match).
 */
export async function resolveAndCompile(queryText, opts = {}) {
  const { adapterResolver } = opts;
  const { ast, errors: parseErrors, warnings } = parseQuery(queryText || "");
  if (!ast) {
    const c = compileQuery(null, { adapterResolver });
    return { ...c, errors: [...parseErrors, ...c.errors], warnings: [...warnings, ...c.warnings], resolved: [], searchedConversations: false };
  }

  // Collect the clauses needing resolution: cluster with a non-numeric (label)
  // value. (endpoint is matched by hostname via a join — see queryLangCompile —
  // so it needs no client-side id resolution.)
  const jobs = [];
  walkPreds(ast, (node) => {
    if (node.key === "topic") {
      const vals = Array.isArray(node.value) ? node.value : [node.value];
      if (!(vals.length && vals.every(isNumeric))) jobs.push({ node, dim: "cluster" });
    }
  });

  // A bare word also searches the captured conversation text, which the sync
  // compiler cannot express: the trace log is rooted at PROMPT and there is no
  // edge from it to the conversation index, so the sessions are resolved here
  // and ORed in as ids.
  const textNodes = [];
  walkText(ast, (node) => textNodes.push(node));

  // Only reach for the real (browser-only) resolver when there's actually a
  // label to resolve and the caller didn't inject its own — keeps the
  // cluster-tree / analytics-client deps off the module-load path.
  let { clusterResolver, conversationResolver } = opts;
  if ((jobs.length || textNodes.length) && (!clusterResolver || !conversationResolver)) {
    const m = await import("./queryLangResolvers.js");
    clusterResolver ??= m.clusterResolver;
    conversationResolver ??= m.conversationResolver;
  }

  const conversationWarnings = [];
  const searchable = Boolean(opts.range?.startTime && opts.range?.endTime);
  let searchedConversations = false;
  await Promise.all(
    textNodes.map(async (node) => {
      let ids;
      let failed = false;
      try {
        ids = await conversationResolver?.(node.value, opts.range ?? {});
      } catch {
        ids = [];
        failed = true;
      }
      const skipped = SKIPPED_CONVERSATION_SEARCH[ids?.skipped];
      if (skipped) {
        conversationWarnings.push({ message: skipped(node.value) });
      } else if (searchable && conversationResolver && !failed) {
        searchedConversations = true;
      }
      node.sessionIds = Array.isArray(ids) ? ids : [];
    }),
  );

  const resolved = [];
  await Promise.all(jobs.map(async ({ node, dim }) => {
    const negate = node.op === "ne"; // `cluster!=label` — preserve the exclusion
    const vals = Array.isArray(node.value) ? node.value : [node.value];
    const lists = await Promise.all(vals.map(async (v) => {
      // A numeric value in a mixed list (`cluster:auth,12`) is already an id —
      // don't send it through the name resolver, which would treat "12" as a
      // label to substring-match and silently drop it.
      if (isNumeric(v)) return [String(v)];
      try { return (await clusterResolver?.(v)) ?? []; } catch { return []; }
    }));
    const ids = [...new Set(lists.flat().map(String))];
    resolved.push({ dim, label: vals.join(","), ids });
    // Rewrite into an id clause: positive → `IN(ids)`; negated → keep `ne` so
    // the compiler wraps it in NOT. Empty → never-match sentinel, so
    // `cluster:nonesuch` is 0 rows and `cluster!=nonesuch` is all rows.
    node.op = negate ? "ne" : "in";
    node.value = ids.length ? ids : [NO_CLUSTER];
    node.resolvedFromLabel = true;
  }));

  const c = compileQuery(ast, { adapterResolver });
  return { ...c, errors: [...parseErrors, ...c.errors], warnings: [...warnings, ...c.warnings, ...conversationWarnings], resolved, searchedConversations };
}
